'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckCircle, Gift, Info, Warning, WarningOctagon, X } from '@phosphor-icons/react';
import type { Url } from 'next/dist/shared/lib/router/router';
import Link from 'next/link';
import type { PropsWithChildren } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import { cx } from '../../cx';
import { useMediaState } from '../../hooks/use-media-state';
import { uiTestId } from '../../test-ids';
import type { ButtonProps } from '../button/button';
import { Button } from '../button/button';
import type { IconButtonProps } from '../button/icon-button';
import { IconButton } from '../button/icon-button';
export type MessageBoxAction = {
  label: string;
  onClick: () => void;
  href?: never;
  newTab?: never;
} | {
  label: string;
  href: Url;
  newTab?: boolean;
  onClick?: () => void;
};
type MessageBoxProps = PropsWithChildren<VariantProps<typeof slots> & {
  className?: string;
  action?: MessageBoxAction;
  onClose?: () => void;
  testId?: string;
}>;
const slots = tv({
  slots: {
    base: 'relative flex min-h-14 items-start gap-2 rounded-2xl p-4',
    content: 'mr-auto grid grid-cols-[max-content_1fr] items-center gap-2',
    message: '',
    action: '',
    closeButton: ''
  },
  variants: {
    size: {
      small: {
        base: 'font-body-small flex-col',
        message: 'pr-11',
        action: 'self-end',
        closeButton: 'absolute right-1.5 top-1.5'
      },
      medium: {
        base: 'font-body-small items-center',
        action: 'order-1',
        closeButton: 'order-2'
      },
      responsive: {
        base: 'font-body-small @sm:font-body-small @sm:flex-row @sm:items-center flex-col',
        message: '@sm:pr-0',
        action: '@sm:self-auto self-end',
        closeButton: '@sm:static absolute right-1.5 top-1.5'
      }
    },
    type: {
      info: 'bg-lightblue',
      success: 'bg-success-bg',
      warning: 'bg-warning-bg text-warning-text',
      error: 'bg-error-bg text-error',
      gift: 'bg-rose'
    }
  }
});
export const MessageBox = t0 => {
  const $ = _c(40);
  const {
    children,
    className,
    type,
    size: t1,
    action,
    onClose,
    testId
  } = t0;
  const size = t1 === undefined ? "responsive" : t1;
  const isMobile = useMediaState("mobile");
  let T0;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== action || $[1] !== children || $[2] !== className || $[3] !== isMobile || $[4] !== onClose || $[5] !== size || $[6] !== testId || $[7] !== type) {
    const {
      base,
      content,
      message,
      action: actionClasses,
      closeButton
    } = slots({
      type,
      size
    });
    const responsiveButtonSize = isMobile ? "small" : "medium";
    const buttonSize = size === "responsive" ? responsiveButtonSize : size;
    t8 = "alert";
    if ($[18] !== className) {
      t9 = cx("@container", className);
      $[18] = className;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    if ($[20] !== testId) {
      t10 = testId ?? uiTestId("MessageBox.root");
      $[20] = testId;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t5 = base();
    let t11;
    if ($[22] !== type) {
      t11 = <Icon type={type} className="size-6 self-start" />;
      $[22] = type;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    t6 = <div className={content()}>{t11}<div className={message({
        className: onClose && "pr-11"
      })}>{children}</div></div>;
    t7 = action && <div className={actionClasses()}><ActionButton action={action} size={buttonSize} /></div>;
    T0 = CloseButton;
    t2 = type;
    t3 = buttonSize;
    t4 = closeButton();
    $[0] = action;
    $[1] = children;
    $[2] = className;
    $[3] = isMobile;
    $[4] = onClose;
    $[5] = size;
    $[6] = testId;
    $[7] = type;
    $[8] = T0;
    $[9] = t10;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    T0 = $[8];
    t10 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[24] !== T0 || $[25] !== onClose || $[26] !== t2 || $[27] !== t3 || $[28] !== t4) {
    t11 = <T0 type={t2} size={t3} className={t4} onClick={onClose} />;
    $[24] = T0;
    $[25] = onClose;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7) {
    t12 = <div className={t5}>{t6}{t7}{t11}</div>;
    $[30] = t11;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t10 || $[36] !== t12 || $[37] !== t8 || $[38] !== t9) {
    t13 = <div role={t8} className={t9} data-testid={t10}>{t12}</div>;
    $[35] = t10;
    $[36] = t12;
    $[37] = t8;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
};
type ActionButtonProps = {
  action: MessageBoxAction;
  size: ButtonProps['size'];
};
const ActionButton = t0 => {
  const $ = _c(11);
  const {
    action,
    size
  } = t0;
  if (action.href) {
    const t1 = action.newTab ? "_blank" : undefined;
    const t2 = action.newTab ? "noopener noreferrer" : undefined;
    let t3;
    if ($[0] !== action.href || $[1] !== action.label || $[2] !== action.onClick || $[3] !== size || $[4] !== t1 || $[5] !== t2) {
      t3 = <Button as={Link} href={action.href} target={t1} rel={t2} size={size} variant="text" color="current" onClick={action.onClick}>{action.label}</Button>;
      $[0] = action.href;
      $[1] = action.label;
      $[2] = action.onClick;
      $[3] = size;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t1;
  if ($[7] !== action.label || $[8] !== action.onClick || $[9] !== size) {
    t1 = <Button size={size} variant="text" color="current" onClick={action.onClick}>{action.label}</Button>;
    $[7] = action.label;
    $[8] = action.onClick;
    $[9] = size;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
};
type CloseButtonProps = {
  type: MessageBoxProps['type'];
  size: IconButtonProps['size'];
  className?: string;
  onClick?: () => void;
};
const CloseButton = t0 => {
  const $ = _c(4);
  const {
    type,
    size,
    className,
    onClick
  } = t0;
  if (!onClick || type === "warning" || type === "error") {
    return null;
  }
  let t1;
  if ($[0] !== className || $[1] !== onClick || $[2] !== size) {
    t1 = <IconButton size={size} className={className} Icon={X} weight="ghost" variant="transparent" onClick={onClick} aria-label={"Hinweis schlie\xDFen"} />;
    $[0] = className;
    $[1] = onClick;
    $[2] = size;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
type IconProps = {
  type: MessageBoxProps['type'];
  className?: string;
};
const Icon = t0 => {
  const $ = _c(10);
  const {
    type,
    className
  } = t0;
  switch (type) {
    case "info":
      {
        let t1;
        if ($[0] !== className) {
          t1 = <Info className={className} />;
          $[0] = className;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "success":
      {
        let t1;
        if ($[2] !== className) {
          t1 = <CheckCircle className={className} />;
          $[2] = className;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "warning":
      {
        let t1;
        if ($[4] !== className) {
          t1 = <Warning className={className} />;
          $[4] = className;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "error":
      {
        let t1;
        if ($[6] !== className) {
          t1 = <WarningOctagon className={className} />;
          $[6] = className;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case "gift":
      {
        let t1;
        if ($[8] !== className) {
          t1 = <Gift className={className} />;
          $[8] = className;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        return t1;
      }
    case undefined:
      {}
    default:
      {
        return null;
      }
  }
};